import { DEFAULT_CONSTANTS, DEFAULT_EV, DEFAULT_DATA, formatNumber, calculateTCO } from '../utils/calculator_helpers'

export default class CalculatorController {
  constructor(element) {
    this.element = element
    this.currentStep = 1
    this.totalSteps = 3

    this.constants = { ...DEFAULT_CONSTANTS }
    this.defaultEV = { ...DEFAULT_EV }

    // Form data with defaults
    this.data = { ...DEFAULT_DATA, ev: { ...this.defaultEV } }

    this.bindEvents()
    this.showStep(1)
    this.updateSliderDisplays()
  }

  bindEvents() {
    // Navigation buttons
    this.element.querySelector('[data-calc-next]')?.addEventListener('click', () => this.nextStep())
    this.element.querySelector('[data-calc-prev]')?.addEventListener('click', () => this.prevStep())
    this.element.querySelector('[data-calc-results]')?.addEventListener('click', () => this.showResults())
    this.element.querySelector('[data-calc-reset]')?.addEventListener('click', () => this.reset())

    // Period buttons
    this.element.querySelectorAll('[data-period]').forEach(btn => {
      btn.addEventListener('click', () => this.selectPeriod(parseInt(btn.dataset.period)))
    })

    // Input fields
    this.element.querySelectorAll('[data-calc-input]').forEach(input => {
      input.addEventListener('input', (e) => this.updateData(e.target.dataset.calcInput, e.target.value))
    })

    // Sliders
    const kmSlider = this.element.querySelector('[data-calc-input="annualKm"]')
    if (kmSlider) {
      kmSlider.addEventListener('input', () => this.updateSliderDisplays())
    }

    const chargeSlider = this.element.querySelector('[data-calc-input="homeChargePercent"]')
    if (chargeSlider) {
      chargeSlider.addEventListener('input', () => this.updateSliderDisplays())
    }
  }

  selectPeriod(years) {
    this.data.period = years

    // Update UI
    this.element.querySelectorAll('[data-period]').forEach(btn => {
      if (parseInt(btn.dataset.period) === years) {
        btn.classList.add('bg-green-500', 'text-white')
        btn.classList.remove('bg-gray-200', 'text-gray-700')
      } else {
        btn.classList.remove('bg-green-500', 'text-white')
        btn.classList.add('bg-gray-200', 'text-gray-700')
      }
    })

    this.updateSliderDisplays()
  }

  updateData(field, value) {
    const numValue = parseFloat(value) || 0
    this.data[field] = numValue
  }

  updateSliderDisplays() {
    // Update km display
    const kmValue = this.element.querySelector('[data-km-value]')
    const kmTotal = this.element.querySelector('[data-km-total]')
    if (kmValue) kmValue.textContent = formatNumber(this.data.annualKm)
    if (kmTotal) kmTotal.textContent = formatNumber(this.data.annualKm * this.data.period)

    // Update charge display
    const homePercent = this.element.querySelector('[data-home-percent]')
    const publicPercent = this.element.querySelector('[data-public-percent]')
    if (homePercent) homePercent.textContent = this.data.homeChargePercent
    if (publicPercent) publicPercent.textContent = 100 - this.data.homeChargePercent
  }

  showStep(step) {
    this.currentStep = step

    // Hide all steps
    this.element.querySelectorAll('[data-step]').forEach(el => {
      el.style.display = 'none'
    })

    // Show current step
    const currentStepEl = this.element.querySelector(`[data-step="${step}"]`)
    if (currentStepEl) currentStepEl.style.display = 'block'

    // Update step indicators
    this.element.querySelectorAll('[data-step-indicator]').forEach(el => {
      const indicatorStep = parseInt(el.dataset.stepIndicator)
      if (indicatorStep < step) {
        el.classList.add('bg-green-500', 'text-white')
        el.classList.remove('bg-gray-200', 'text-gray-500', 'border-green-500')
      } else if (indicatorStep === step) {
        el.classList.add('border-green-500', 'text-green-500')
        el.classList.remove('bg-green-500', 'text-white', 'bg-gray-200', 'text-gray-500')
      } else {
        el.classList.add('bg-gray-200', 'text-gray-500')
        el.classList.remove('bg-green-500', 'text-white', 'border-green-500', 'text-green-500')
      }
    })

    // Update navigation buttons
    const prevBtn = this.element.querySelector('[data-calc-prev]')
    const nextBtn = this.element.querySelector('[data-calc-next]')
    const resultsBtn = this.element.querySelector('[data-calc-results]')
    const resetBtn = this.element.querySelector('[data-calc-reset]')

    if (prevBtn) prevBtn.style.display = step > 1 && step < 3 ? 'inline-flex' : 'none'
    if (nextBtn) nextBtn.style.display = step < 2 ? 'inline-flex' : 'none'
    if (resultsBtn) resultsBtn.style.display = step === 2 ? 'inline-flex' : 'none'
    if (resetBtn) resetBtn.style.display = step === 3 ? 'inline-flex' : 'none'
  }

  nextStep() {
    if (this.currentStep < this.totalSteps) {
      this.showStep(this.currentStep + 1)
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.showStep(this.currentStep - 1)
    }
  }

  showResults() {
    // Read all input values before calculating
    this.element.querySelectorAll('[data-calc-input]').forEach(input => {
      this.updateData(input.dataset.calcInput, input.value)
    })

    const results = calculateTCO(this.data, this.constants)
    this.renderResults(results)
    this.showStep(3)
  }

  renderResults(r) {
    const resultsContainer = this.element.querySelector('[data-results]')
    if (!resultsContainer) return

    const savingsClass = r.totalSavings > 0 ? 'text-green-400' : 'text-red-400'
    const savingsText = r.totalSavings > 0 ? 'El electrico te conviene!' : 'El auto a nafta es mas economico'

    resultsContainer.innerHTML = `
      <div class="text-center mb-4">
        <p class="text-sm text-muted-foreground">${r.ev.name} vs Auto a nafta</p>
        <p class="text-xs text-muted-foreground">${r.period} años - ${formatNumber(r.annualKm)} km/año</p>
      </div>

      <div class="bg-primary/10 rounded-lg p-4 mb-4 text-center border border-primary/30">
        <p class="${savingsClass} text-sm font-semibold mb-1">${savingsText}</p>
        <p class="text-2xl font-bold ${savingsClass}">
          ${r.totalSavings > 0 ? 'Ahorras' : 'Gastas'} US$ ${formatNumber(Math.abs(r.totalSavings / r.exchangeRate))}
        </p>
        <p class="text-xs text-muted-foreground">($ ${formatNumber(Math.abs(r.totalSavings))}) en ${r.period} años</p>
      </div>

      <div class="grid grid-cols-2 gap-3 mb-4">
        <div class="bg-white/5 rounded-lg p-3 border border-white/10">
          <p class="text-xs text-primary mb-1">Electrico</p>
          <p class="text-white font-bold">$ ${formatNumber(r.evTotalCost)}</p>
          <p class="text-[10px] text-muted-foreground">US$ ${formatNumber(r.evTotalCost / r.exchangeRate)}</p>
        </div>
        <div class="bg-white/5 rounded-lg p-3 border border-white/10">
          <p class="text-xs text-orange-400 mb-1">Nafta</p>
          <p class="text-white font-bold">$ ${formatNumber(r.gasTotalCost)}</p>
          <p class="text-[10px] text-muted-foreground">US$ ${formatNumber(r.gasTotalCost / r.exchangeRate)}</p>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div class="bg-white/5 rounded-lg p-3 text-center border border-white/10">
          <p class="text-[10px] text-muted-foreground">Equilibrio</p>
          <p class="text-lg font-bold text-primary">${r.breakevenYears ? r.breakevenYears.toFixed(1) + ' años' : 'N/A'}</p>
        </div>
        <div class="bg-white/5 rounded-lg p-3 text-center border border-white/10">
          <p class="text-[10px] text-muted-foreground">Ahorro mensual</p>
          <p class="text-lg font-bold text-green-400">+$ ${formatNumber(r.monthlySavings)}</p>
        </div>
      </div>
    `
  }

  reset() {
    this.selectPeriod(5)
    this.showStep(1)
  }
}

// Auto-initialize calculators
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-controller="calculator"]').forEach(el => {
    new CalculatorController(el)
  })
})
